import { IComboSlot, IItem, IItemOption } from '@rbi-ctg/menu';
import { stabilizeModifierGroupSelections } from 'components/product-customization/static-layout';
import { TranslationKey } from 'state/intl/types';
import { UserSelectionModifierSlotMap } from 'state/product-wizard-v2/types';
import { isItem } from 'utils/menu';

import { IModifierSelections } from '../../modifier/types';
import { ComboSlotType } from '../ComboSlotType';
import { ProductHierarchyType } from '../types';
import { groupModifiersByDisplayName as groupModifiersByDisplayNameMap } from '../utils';

import { IModifierGroup } from './types';

function areModifierSelectionsEqual(
  previous: IModifierSelections,
  next: IModifierSelections
): boolean {
  const previousKeys = Object.keys(previous);
  const nextKeys = Object.keys(next);

  if (previousKeys.length !== nextKeys.length) {
    return false;
  }

  for (const key of previousKeys) {
    const previousSelection = previous[key];
    const nextSelection = next[key];

    if (
      previousSelection?._key !== nextSelection?._key ||
      previousSelection?.quantity !== nextSelection?.quantity
    ) {
      return false;
    }
  }

  return true;
}

export function stabilizeModifierGroupSelectionsV2(
  groups: IModifierGroup[],
  cache: Map<string, IModifierSelections>
): IModifierGroup[] {
  return stabilizeModifierGroupSelections(
    groups,
    cache,
    (modifier: IModifierGroup['modifiers'][number]['modifier']) => modifier._key,
    areModifierSelectionsEqual
  );
}

const MODIFIER_GROUP_ORDER = ['included', 'addons', 'chooseItemAmount'] as const;

const getItemHierarchySlotType = (
  item: IItem
): ComboSlotType.SIDES | ComboSlotType.DRINKS | null => {
  if (!item.productHierarchy) {
    return null;
  }

  const { productHierarchy } = item;

  if (productHierarchy.L1 !== ProductHierarchyType.SIDES_OR_DRINKS) {
    return null;
  }

  if (productHierarchy.L2 === ProductHierarchyType.SIDES) {
    return ComboSlotType.SIDES;
  }

  if (
    productHierarchy.L2 === ProductHierarchyType.DRINKS ||
    (productHierarchy.L2 === ProductHierarchyType.DESSERTS &&
      productHierarchy.L3 === ProductHierarchyType.SHAKES)
  ) {
    return ComboSlotType.DRINKS;
  }

  return null;
};

export const getComboSlotType = (comboSlot: IComboSlot | null): ComboSlotType | null => {
  if (!comboSlot?.options?.length) {
    return null;
  }

  let hasSides = false;
  let hasDrinks = false;

  for (const comboSlotOption of comboSlot.options) {
    const { option } = comboSlotOption;

    if (!isItem(option)) {
      continue;
    }

    const hierarchySlotType = getItemHierarchySlotType(option);

    if (hierarchySlotType === ComboSlotType.SIDES) {
      hasSides = true;
    } else if (hierarchySlotType === ComboSlotType.DRINKS) {
      hasDrinks = true;
    }
  }

  // Some products combine both sides and drinks
  if (hasDrinks && !hasSides) {
    return ComboSlotType.DRINKS;
  }
  if (hasSides && !hasDrinks) {
    return ComboSlotType.SIDES;
  }

  return null;
};

export function groupModifiersByDisplayName(
  modifiers: IItemOption[],
  modifierSelections: UserSelectionModifierSlotMap,
  pickerAspectExclusions?: Set<string>
): IModifierGroup[] {
  const modifierGroupsMap = groupModifiersByDisplayNameMap({
    modifiers,
    modifiersMap: modifierSelections,
    pickerAspectExclusions,
  });

  return MODIFIER_GROUP_ORDER.reduce<IModifierGroup[]>((groups, groupKey) => {
    const groupModifiers = modifierGroupsMap[groupKey];

    if (!groupModifiers?.length) {
      return groups;
    }

    groups.push({
      key: groupKey,
      ...(groupKey !== 'chooseItemAmount' ? { displayGroupName: groupKey as TranslationKey } : {}),
      modifiers: groupModifiers,
    });

    return groups;
  }, []);
}
